import { Link } from 'react-router-dom'
import { articles } from '@/pages/blog/articles'

interface RelatedArticlesProps {
  currentSlug: string
}

/**
 * Shows 3 related articles at the bottom of each blog post.
 * Selection: articles with most keyword overlap, then by recency.
 * Purpose: SEO — builds topical authority via internal linking.
 */
export function RelatedArticles({ currentSlug }: RelatedArticlesProps) {
  const current = articles.find(a => a.slug === currentSlug)
  if (!current) return null

  const currentKeywords = new Set(current.keywords.map(k => k.toLowerCase()))

  // Score each other article by keyword overlap, then sort by score desc, then recency desc
  const related = articles
    .filter(a => a.slug !== currentSlug)
    .map(a => {
      const overlap = a.keywords.filter(k =>
        [...currentKeywords].some(ck => ck.includes(k.toLowerCase()) || k.toLowerCase().includes(ck))
      ).length
      return { article: a, score: overlap }
    })
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score
      return b.article.publishedAt.localeCompare(a.article.publishedAt)
    })
    .slice(0, 3)
    .map(r => r.article)

  if (related.length === 0) return null

  return (
    <section className="mt-12 pt-8 border-t">
      <h2 className="text-lg font-bold text-foreground mb-4">Przeczytaj także</h2>
      <div className="grid gap-3 sm:grid-cols-3">
        {related.map(article => (
          <Link
            key={article.slug}
            to={`/blog/${article.slug}`}
            className="group p-4 rounded-xl border border-border hover:border-primary/40 hover:bg-muted/30 transition-colors"
          >
            <h3 className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors leading-snug mb-2">
              {article.title}
            </h3>
            <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
              {article.description}
            </p>
          </Link>
        ))}
      </div>
    </section>
  )
}
